// src/components/LoginCodeStep.jsx
//
// Second step of every password login (AdminLogin, ClientLogin,
// UnifiedLogin). The server answered the password with
// { mfa_required, challenge_id, email_hint, resend_after } instead of
// tokens; this screen collects the 6-digit emailed code and exchanges it
// for the real login response via /api/auth/mfa/verify.
//
// Two methods, decided by the server per account:
//  - 'email': code emailed now; resend available.
//  - 'totp' : code from the user's authenticator app, or a backup code.
//    No resend and no email fallback.
//
// onVerified(data) gets exactly what the old one-step login returned
// ({ user, refresh_token, ... }), so the parent's success path is unchanged.
// Setting up an authenticator is suggested after login by TwoFactorPrompt.
import React, { useState, useEffect, useRef } from 'react';
import '../styles/Login.css';
import logo from '../logo512.png';
import { apiFetch } from '../api/apiClient';

async function postJSON(url, body) {
  const res = await apiFetch(url, {
    method: 'POST',
    skipAuthRefresh: true, // no session yet - a 401 here is a wrong code, not an expired token
    body: JSON.stringify(body),
  });
  let data = {};
  try { data = await res.json(); } catch { /* empty body */ }
  return { ok: res.ok, data };
}

const LoginCodeStep = ({ challenge, onVerified, onCancel, containerClass = '' }) => {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [dead, setDead] = useState(false); // challenge expired/used up -> must log in again
  const isTotp = challenge.method === 'totp';
  const [useBackup, setUseBackup] = useState(false); // totp: typing a backup code instead
  const [resendIn, setResendIn] = useState(isTotp ? 0 : (challenge.resend_after || 30));
  const codeLength = useBackup ? 8 : 6;
  const busyRef = useRef(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const t = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const verify = async (value) => {
    if (busyRef.current || dead) return;
    busyRef.current = true;
    setVerifying(true);
    setError('');
    setInfo('');
    try {
      const { ok, data } = await postJSON('/api/auth/mfa/verify', {
        challenge_id: challenge.challenge_id,
        code: value,
      });
      if (ok) {
        onVerified(data);
        return;
      }
      setError(data.error || 'Verification failed');
      if (data.restart) setDead(true);
      setCode('');
      inputRef.current?.focus();
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      busyRef.current = false;
      setVerifying(false);
    }
  };

  const handleChange = (e) => {
    if (useBackup) {
      // Backup codes look like ABCD-EFGH; the server ignores the dash/case
      const v = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8);
      setCode(v.length > 4 ? `${v.slice(0, 4)}-${v.slice(4)}` : v);
      return;
    }
    const digits = e.target.value.replace(/\D/g, '').slice(0, 6);
    setCode(digits);
    // Auto-submit on the 6th digit (also covers iOS "From Mail" autofill)
    if (digits.length === 6) verify(digits);
  };

  const typedLength = code.replace(/-/g, '').length;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (typedLength === codeLength) verify(code);
  };

  const toggleBackup = () => {
    setUseBackup((b) => !b);
    setCode('');
    setError('');
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const resend = async () => {
    setError('');
    setInfo('');
    try {
      const { ok, data } = await postJSON('/api/auth/mfa/resend', {
        challenge_id: challenge.challenge_id,
      });
      if (ok) {
        setInfo(`A new code was sent to ${challenge.email_hint}.`);
        setResendIn(data.resend_after || 30);
        setCode('');
        inputRef.current?.focus();
      } else {
        setError(data.error || 'Could not resend the code');
        if (data.retry_after) setResendIn(data.retry_after);
        if (data.restart) setDead(true);
      }
    } catch {
      setError('Network error. Please try again.');
    }
  };

  return (
    <div className="login-page">
      <div className={`login-container ${containerClass}`}>
        <div className="login-header">
          <img src={logo} className="login-logo-247" alt="247 Logo" />
          <div>
            <h2>{isTotp ? 'Authenticator code' : 'Check your email'}</h2>
            <p>Login verification</p>
          </div>
        </div>

        <p className="login-code-hint">
          {isTotp ? (
            useBackup
              ? <>Enter one of the backup codes you saved when you set up your authenticator app. Each code works once.</>
              : <>Open your authenticator app and enter the 6-digit code for <b>Twenty4Seven</b>.</>
          ) : (
            <>We sent a 6-digit code to <b>{challenge.email_hint}</b>.
            It expires in {Math.round((challenge.expires_in || 600) / 60)} minutes.</>
          )}
        </p>

        {error && (
          <div className="login-error">
            <span className="login-error-icon">!</span>
            <div>{error}</div>
          </div>
        )}
        {info && !error && <div className="login-code-info">{info}</div>}

        {dead ? (
          <button type="button" className="login-btn" onClick={onCancel}>
            Back to login
          </button>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="login-code">{useBackup ? 'Backup code' : 'Login code'}</label>
              <input
                ref={inputRef}
                id="login-code"
                className={'login-code-input' + (useBackup ? ' login-code-input-backup' : '')}
                type="text"
                inputMode={useBackup ? 'text' : 'numeric'}
                autoComplete="one-time-code"
                autoCapitalize="characters"
                maxLength={useBackup ? 9 : 6}
                placeholder={useBackup ? 'XXXX-XXXX' : '••••••'}
                value={code}
                onChange={handleChange}
                disabled={verifying}
              />
            </div>

            <button
              type="submit"
              className="login-btn"
              disabled={verifying || typedLength !== codeLength}
            >
              {verifying ? (<><span className="spinner-icon">↻</span>Verifying...</>) : 'Verify'}
            </button>

            <div className="login-code-actions">
              {isTotp ? (
                <button type="button" className="login-link-btn" onClick={toggleBackup} disabled={verifying}>
                  {useBackup ? 'Use authenticator code' : 'Lost your phone? Use a backup code'}
                </button>
              ) : (
                <button
                  type="button"
                  className="login-link-btn"
                  onClick={resend}
                  disabled={resendIn > 0 || verifying}
                >
                  {resendIn > 0 ? `Resend code in ${resendIn}s` : 'Resend code'}
                </button>
              )}
              <button type="button" className="login-link-btn" onClick={onCancel} disabled={verifying}>
                Back to login
              </button>
            </div>
          </form>
        )}

        <div className="login-info">
          <p>{isTotp
            ? 'No phone and no backup codes? Ask your administrator to reset your 2FA.'
            : "Didn't get it? Check your spam folder."}<br />
          <a href="mailto:support@twenty4seven.io">support@twenty4seven.io</a></p>
        </div>
      </div>
    </div>
  );
};

export default LoginCodeStep;
